import type { Locale } from "../i18n";

const copy = {
	ja: {
		myPage: {
			metaTitle: "リンク管理 - GSplat Share",
			metaDescription: "作成した共有リンクを確認、編集、削除できます。",
			title: "リンク管理",
			description: "ログイン後に作成した共有リンクを確認、編集、削除できます。",
			upload: "アップロード",
			logout: "ログアウト",
			checking: "ログイン状態を確認しています。",
			linksFailed: "共有リンクを読み込めませんでした。もう一度お試しください。",
			retry: "もう一度読み込む",
			checkFailedTitle: "ログイン状態を確認できませんでした",
			checkFailedBody: "サービスに接続できませんでした。もう一度お試しください。",
			loggingOut: "ログアウト中…",
			offline: "ブラウザがオフラインです。接続が戻ってから、もう一度お試しください。",
			logoutFailed: "ログアウトできませんでした。もう一度お試しください。",
			developerDetails: "開発者向け詳細",
			developerHint: "/share/auth/* と /share/trpc/* が API Worker に届いているか確認してください。",
		},
		signedOut: {
			title: "Googleでログインすると、作ったリンクが残ります。",
			titleLines: ["Googleでログインすると、", "作ったリンクが残ります。"],
			description:
				"ログイン中にアップロードしたデータは、自動でリンク管理に追加されます。あとからタイトル・説明・公開範囲・公開期限を変更できます。",
			accountHint: "すでに作成したリンクを管理したい場合も、同じGoogleアカウントでログインしてください。",
			artLabel: "空間のライブラリ",
			artCaption: "共有した3Dデータを、ここから確認。",
			continueWithoutLogin: "ログインせずに共有する",
			googleLogin: "Googleでログイン",
			devLogin: "開発用ログイン",
		},
		library: {
			title: "共有リンク",
			description: "保存済みのリンクです。タイトル、説明、公開範囲、公開期限を変更できます。",
			loading: "リンクを読み込んでいます。",
			emptyTitle: "共有リンクはまだありません",
			emptyBody: "ファイルをアップロードして共有リンクを作成すると、ここに表示されます。",
			upload: "ファイルをアップロード",
			untitled: "タイトルなし",
			views: "回閲覧",
			copy: "コピー",
			copied: "コピーしました",
			open: "開く",
			more: "その他",
			edit: "編集",
			delete: "削除",
			save: "保存",
			saving: "保存中…",
			saved: "変更を保存しました。",
			saveFailed: "変更を保存できませんでした。入力は残っています。もう一度お試しください。",
			copyFailed: "コピーできませんでした。リンクを開き、アドレスをコピーしてください。",
			deleting: "削除中…",
			deleted: "共有リンクを削除しました。",
			deleteFailed: "削除できませんでした。リンクは一覧に残っています。もう一度お試しください。",
			deleteBody: "削除すると、この共有リンクは開けなくなります。この操作は取り消せません。",
			unsaved: "未保存の変更があります",
			discardTitle: "変更を破棄しますか？",
			discardBody: "編集中の内容は保存されていません。",
			discard: "変更を破棄",
			keepEditing: "編集を続ける",
			cancel: "キャンセル",
			fieldTitle: "タイトル",
			fieldTitlePlaceholder: "タイトルを入力",
			fieldDescription: "説明",
			fieldDescriptionPlaceholder: "説明を入力",
			visibility: "公開範囲",
			visibilityHint: "非公開は所有者と管理者だけが開けます。",
			expiration: "公開期限",
			currentExpiration: "現在",
			expires: "期限",
			expired: "期限切れ",
			expirationUnlimited: "無期限",
			expirationKeep: "変更しない",
			extendHour: "1時間延長",
			extendDay: "1日延長",
			extendWeek: "1週間延長",
			makeUnlimited: "無期限にする",
			expirationHint: "有効なリンクは現在の期限から延長します。期限切れのリンクは現在時刻から再開します。",
			visibilityLabels: {
				public: "公開",
				unlisted: "限定公開",
				private: "非公開",
			},
			confirmDelete: (title: string) => `「${title}」を削除しますか？`,
		},
		admin: {
			metaTitle: "管理 - GSplat Share",
			metaDescription: "GSplat Share の管理ページです。",
			eyebrow: "Admin",
			title: "管理",
			description: "登録ユーザーを確認できます。",
			checking: "ログイン状態を確認しています。",
			loginRequiredTitle: "ログインが必要です",
			loginRequiredBody: "管理ページを見るにはログインしてください。",
			login: "Googleでログイン",
			forbiddenTitle: "権限がありません",
			forbiddenBody: "このページは管理者だけが利用できます。",
			loading: "ユーザーを読み込んでいます。",
			failed: "ユーザーを読み込めませんでした。もう一度お試しください。",
			empty: "登録ユーザーはいません",
			identity: "ユーザー",
			role: "権限",
			created: "登録日",
		},
	},
	en: {
		myPage: {
			metaTitle: "Manage links - GSplat Share",
			metaDescription: "Review, edit, and delete created share links.",
			title: "Manage links",
			description: "Review, edit, and delete share links created after sign-in.",
			upload: "Upload",
			logout: "Log out",
			checking: "Checking sign-in status.",
			linksFailed: "Could not load your share links. Please try again.",
			retry: "Try again",
			checkFailedTitle: "Could not check sign-in status",
			checkFailedBody: "We could not connect to the service. Please try again.",
			loggingOut: "Logging out…",
			offline: "Your browser is offline. Try again when the connection returns.",
			logoutFailed: "Could not log out. Please try again.",
			developerDetails: "Developer details",
			developerHint: "Confirm /share/auth/* and /share/trpc/* reach the API Worker.",
		},
		signedOut: {
			title: "Your links, in one place.",
			titleLines: ["Your links,", "in one place."],
			description:
				"Sign in with Google to find your share links and update their titles, descriptions, visibility, and expiration.",
			accountHint: "Files uploaded while signed in will appear here.",
			artLabel: "A spatial library",
			artCaption: "Return to the places you captured.",
			continueWithoutLogin: "Share without signing in",
			googleLogin: "Sign in with Google",
			devLogin: "Development sign-in",
		},
		library: {
			title: "Share links",
			description: "Saved links. Edit title, description, visibility, and expiration.",
			loading: "Loading links.",
			emptyTitle: "No saved links yet",
			emptyBody: "Links created while signed in will appear here.",
			upload: "Upload a file",
			untitled: "Untitled",
			views: "views",
			copy: "Copy",
			copied: "Copied",
			open: "Open",
			more: "More",
			edit: "Edit",
			delete: "Delete",
			save: "Save",
			saving: "Saving…",
			saved: "Changes saved.",
			saveFailed: "Could not save changes. Your edits are still here. Please try again.",
			copyFailed: "Could not copy. Open the link and copy its address instead.",
			deleting: "Deleting…",
			deleted: "Share link deleted.",
			deleteFailed: "Could not delete the link. It remains in your library. Please try again.",
			deleteBody: "This share link will stop working. This action cannot be undone.",
			unsaved: "Unsaved changes",
			discardTitle: "Discard changes?",
			discardBody: "Your edits have not been saved.",
			discard: "Discard changes",
			keepEditing: "Keep editing",
			cancel: "Cancel",
			fieldTitle: "Title",
			fieldTitlePlaceholder: "Enter a title",
			fieldDescription: "Description",
			fieldDescriptionPlaceholder: "Enter a description",
			visibility: "Visibility",
			visibilityHint: "Private links can be opened only by the owner and admins.",
			expiration: "Expiration",
			currentExpiration: "Current",
			expires: "Expires",
			expired: "Expired",
			expirationUnlimited: "Unlimited",
			expirationKeep: "No change",
			extendHour: "Extend by 1 hour",
			extendDay: "Extend by 1 day",
			extendWeek: "Extend by 1 week",
			makeUnlimited: "Make unlimited",
			expirationHint: "Active links extend from their current expiration. Expired links restart from now.",
			visibilityLabels: {
				public: "Public",
				unlisted: "Unlisted",
				private: "Private",
			},
			confirmDelete: (title: string) => `Delete "${title}"?`,
		},
		admin: {
			metaTitle: "Admin - GSplat Share",
			metaDescription: "Admin page for GSplat Share.",
			eyebrow: "Admin",
			title: "Admin",
			description: "Review registered users.",
			checking: "Checking sign-in status.",
			loginRequiredTitle: "Sign-in required",
			loginRequiredBody: "Sign in to view this page.",
			login: "Sign in with Google",
			forbiddenTitle: "Access denied",
			forbiddenBody: "Only admins can use this page.",
			loading: "Loading users.",
			failed: "Could not load users. Please try again.",
			empty: "No registered users",
			identity: "User",
			role: "Role",
			created: "Joined",
		},
	},
} as const;

export function getAccountCopy(locale: Locale) {
	return copy[locale];
}
